// My Music Benefit — the employee-facing card for the employer-sponsored music
// benefit. The employer pays; the employee activates against their OWN personal
// provider account. Privacy is stated plainly and no listening data is ever shown.
// Provider-neutral; uses the shared design tokens/components. Renders nothing when
// the employee has no music entitlement.
import React, { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { Music, Loader2, ExternalLink, Link2, ShieldCheck } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/features/shared/components/ui/card';
import { Badge } from '@/features/shared/components/ui/badge';
import { Button } from '@/features/shared/components/ui/button';
import { getApiErrorMessage } from '@/lib/apiError';
import { useEntitlements } from '@/features/entitlements/useEntitlements';
import { openProviderUrl } from '@/lib/providerLinks';
import {
  getMyMusicBenefit, getMusicLink, beginMusicLink, completeMusicLink,
  activateMusicBenefit, declineMusicBenefit, openMusicPlayback, disconnectMusicLink,
  type MusicEntitlement, type MusicProviderLink,
} from '@/lib/hrApi';

// Client-side capability gate (requirement 1): the music benefit is a strict,
// fail-closed product entitlement. When the tenant is not entitled the card renders
// nothing AND makes NO subscription API request — a 404 is never used as feature
// discovery. The benefit is currently deferred, so this is off for every tenant.
const MUSIC_PRODUCT_CODE = 'music_benefit';

const LABEL: Record<string, string> = {
  ELIGIBLE: 'Available', INVITED: 'Invited', ACTIVATION_PENDING: 'Activating',
  ACTIVE: 'Active', SUSPENDED: 'Paused', EXPIRING: 'Expiring soon',
  EXPIRED: 'Ended', REVOKED: 'Ended', DECLINED: 'Declined',
};
const TONE: Record<string, string> = {
  ACTIVE: 'bg-success-soft text-success-strong', ELIGIBLE: 'bg-accent text-accent-foreground',
  INVITED: 'bg-accent text-accent-foreground', EXPIRING: 'bg-warning-soft text-warning-strong',
  SUSPENDED: 'bg-warning-soft text-warning-strong',
};

export const MyMusicBenefitCard: React.FC = () => {
  const { hasProductStrict } = useEntitlements();
  const entitled = hasProductStrict(MUSIC_PRODUCT_CODE);
  const [ent, setEnt] = useState<MusicEntitlement | null>(null);
  const [link, setLink] = useState<MusicProviderLink | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    // Never call the subscription API unless the tenant is entitled (fail-closed).
    if (!entitled) { setLoading(false); return; }
    try {
      const [ents, l] = await Promise.all([getMyMusicBenefit(), getMusicLink()]);
      // v1: one music benefit per employee — show the most relevant entitlement.
      const primary = ents.find((e) => e.status === 'ACTIVE')
        || ents.find((e) => e.status === 'INVITED' || e.status === 'ELIGIBLE')
        || ents[0] || null;
      setEnt(primary);
      setLink(l);
    } catch {
      setEnt(null);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { void load(); }, [entitled]);

  const guard = async (fn: () => Promise<unknown>, ok?: string) => {
    setBusy(true);
    try { await fn(); if (ok) toast.success(ok); await load(); }
    catch (e) { toast.error(getApiErrorMessage(e, 'Something went wrong')); }
    finally { setBusy(false); }
  };

  const connected = link?.status === 'connected';

  // Connect the personal provider account. A real provider redirects to begin.url;
  // the sandbox completes inline so the flow is demoable end-to-end.
  const connect = () => guard(async () => {
    const state = (crypto?.randomUUID?.() || String(Date.now()));
    await beginMusicLink({ state });
    await completeMusicLink({ code: 'sandbox', state });
  }, 'Account connected');

  const activate = () => guard(() => activateMusicBenefit(ent!.id), 'Music benefit activated');
  const decline = () => guard(() => declineMusicBenefit(ent!.id));
  const disconnect = () => guard(() => disconnectMusicLink(), 'Account disconnected');
  const open = () => guard(async () => {
    const { url } = await openMusicPlayback();
    // Validate before opening: HTTPS + allowlisted provider host + resource pattern.
    if (!url || !openProviderUrl(url)) toast.error('This link can’t be opened.');
  });

  if (!entitled || loading || !ent) return null; // not entitled / no benefit → render nothing
  const status = ent.status;

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between gap-2 pb-3">
        <CardTitle className="flex items-center gap-2 text-base">
          <Music className="h-5 w-5 text-primary-500" /> Music benefit
        </CardTitle>
        <Badge className={`${TONE[status] || 'bg-muted text-muted-foreground'} border-0`}>{LABEL[status] || status}</Badge>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="flex items-start gap-2 text-xs text-muted-foreground">
          <ShieldCheck className="mt-0.5 h-4 w-4 shrink-0 text-success-strong" />
          <span><span className="font-medium text-foreground">Paid by your employer.</span> Your listening activity stays private — your employer can never see your tracks, playlists, or history.</span>
        </div>

        {(status === 'ELIGIBLE' || status === 'INVITED') && (
          <div className="space-y-2">
            {!connected ? (
              <Button size="sm" disabled={busy} onClick={connect}>
                {busy ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <Link2 className="mr-1.5 h-4 w-4" />}
                Connect your music account
              </Button>
            ) : (
              <Button size="sm" disabled={busy} onClick={activate}>
                {busy ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <Music className="mr-1.5 h-4 w-4" />}
                Activate benefit
              </Button>
            )}
            <button type="button" onClick={decline} disabled={busy}
              className="block text-xs text-muted-foreground hover:text-foreground disabled:opacity-50">Not now</button>
          </div>
        )}

        {status === 'ACTIVE' && (
          <div className="flex flex-wrap items-center gap-2">
            <Button size="sm" variant="outline" disabled={busy} onClick={open}>
              <ExternalLink className="mr-1.5 h-4 w-4" /> Open music
            </Button>
            {connected && (
              <button type="button" onClick={disconnect} disabled={busy}
                className="text-xs text-muted-foreground hover:text-foreground disabled:opacity-50">Disconnect account</button>
            )}
          </div>
        )}

        {(status === 'EXPIRING') && (
          <p className="text-xs text-muted-foreground">Your sponsorship is ending soon. You can keep your personal account and playlists afterwards.</p>
        )}
        {(status === 'EXPIRED' || status === 'REVOKED') && (
          <p className="text-xs text-muted-foreground">This benefit is no longer sponsored. Your personal account and playlists are unaffected.</p>
        )}
      </CardContent>
    </Card>
  );
};
